import React, { useState } from 'react';
import { submitToFormspree } from '../utils/formspree';

function CareerForm({ initialPosition = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: initialPosition || '',
    otherPosition: '',
    experience: '',
    isLicensed: false,
    licenseNumber: '',
    licenseState: '',
    isCertified: false,
    certifications: '',
    education: '',
    workHistory: '',
    availability: '',
    portfolioUrl: '',
    coverLetter: '',
    honeypot: '' // Honeypot field to catch bots
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (formData.honeypot) {
      setSubmitStatus({ success: true, message: 'Application submitted successfully!' });
      setIsSubmitting(false);
      return;
    }

    const positionLabel =
      formData.position === 'other'
        ? formData.otherPosition
        : formData.position;

    try {
      await submitToFormspree({
        formType: 'Career Application',
        subject: `Career application: ${positionLabel} from ${formData.name}`,
        fields: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          position: positionLabel,
          otherPosition: formData.otherPosition,
          experience: formData.experience,
          isLicensed: formData.isLicensed,
          licenseNumber: formData.licenseNumber,
          licenseState: formData.licenseState,
          isCertified: formData.isCertified,
          certifications: formData.certifications,
          education: formData.education,
          workHistory: formData.workHistory,
          availability: formData.availability,
          portfolioUrl: formData.portfolioUrl,
          coverLetter: formData.coverLetter,
        },
      });

      setSubmitStatus({ success: true, message: 'Application submitted successfully! We will contact you soon.' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        otherPosition: '',
        experience: '',
        isLicensed: false,
        licenseNumber: '',
        licenseState: '',
        isCertified: false,
        certifications: '',
        education: '',
        workHistory: '',
        availability: '',
        portfolioUrl: '',
        coverLetter: '',
        honeypot: '',
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitStatus({ success: false, message: error.message || 'Failed to submit application' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-lg">
      {submitStatus && (
        <div className={`p-4 mb-6 rounded-lg ${submitStatus.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {submitStatus.message}
        </div>
      )}

      <form className="space-y-6" onSubmit={handleSubmit}>
        <h3 className="text-xl font-semibold mb-4">Personal Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="name">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="Your full name"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="email">
              Email *
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="Your email address"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="phone">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="Your phone number"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="position">
              Position *
            </label>
            <select
              id="position"
              value={formData.position}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              required
            >
              <option value="">Select a position</option>
              <option value="hair-replacement-specialist">Hair Replacement Specialist</option>
              <option value="maintenance-technician">Hair System Maintenance Technician</option>
              <option value="cut-in-blending-specialist">Cut-In and Blending Specialist</option>
              <option value="client-care-coordinator">Client Care Coordinator</option>
              <option value="care-educator">Hair System Care Educator</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {formData.position === 'other' && (
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="otherPosition">
              Specify Position *
            </label>
            <input
              type="text"
              id="otherPosition"
              value={formData.otherPosition}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="Specify the position you're applying for"
              required={formData.position === 'other'}
            />
          </div>
        )}

        <div>
          <label className="block text-gray-700 mb-2" htmlFor="experience">
            Years of Experience *
          </label>
          <input
            type="number"
            id="experience"
            value={formData.experience}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
            placeholder="Years of experience"
            min="0"
            required
          />
        </div>

        <h3 className="text-xl font-semibold mb-4 pt-4">Licensing & Certification</h3>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isLicensed"
              checked={formData.isLicensed}
              onChange={handleChange}
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="isLicensed" className="text-gray-700">
              I am licensed in my field
            </label>
          </div>

          {formData.isLicensed && (
            <div className="grid md:grid-cols-2 gap-6 pl-7">
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="licenseNumber">
                  License Number
                </label>
                <input
                  type="text"
                  id="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
                  placeholder="Your license number"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2" htmlFor="licenseState">
                  State Issued
                </label>
                <input
                  type="text"
                  id="licenseState"
                  value={formData.licenseState}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
                  placeholder="State where license was issued"
                />
              </div>
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isCertified"
              checked={formData.isCertified}
              onChange={handleChange}
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="isCertified" className="text-gray-700">
              I have additional certifications
            </label>
          </div>

          {formData.isCertified && (
            <div className="pl-7">
              <label className="block text-gray-700 mb-2" htmlFor="certifications">
                List Your Certifications
              </label>
              <textarea
                id="certifications"
                value={formData.certifications}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
                placeholder="List your certifications and when you received them"
                rows="3"
              />
            </div>
          )}
        </div>

        <h3 className="text-xl font-semibold mb-4 pt-4">Education & Experience</h3>
        <div className="space-y-6">
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="education">
              Education
            </label>
            <textarea
              id="education"
              value={formData.education}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="List your relevant education and training"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="workHistory">
              Work History
            </label>
            <textarea
              id="workHistory"
              value={formData.workHistory}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="Briefly describe your relevant work experience"
              rows="4"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="availability">
              Availability
            </label>
            <textarea
              id="availability"
              value={formData.availability}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="What days and hours are you available to work?"
              rows="2"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="portfolioUrl">
              Portfolio URL (Optional)
            </label>
            <input
              type="url"
              id="portfolioUrl"
              value={formData.portfolioUrl}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="Link to work examples or professional references"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2" htmlFor="coverLetter">
              Additional Information
            </label>
            <textarea
              id="coverLetter"
              value={formData.coverLetter}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-primary-500"
              placeholder="Tell us why you're interested in this position and why you'd be a good fit"
              rows="5"
            />
          </div>
        </div>

        {/* Honeypot field - hidden from users but bots might fill it */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="honeypot">Leave this empty</label>
          <input
            type="text"
            id="honeypot"
            name="honeypot"
            value={formData.honeypot || ''}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        <div className="flex justify-center">
          <button
            type="submit"
            className="bg-primary-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-primary-700 transition duration-300 disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CareerForm;
